<template>
  <FormulateForm v-if="!isLoading" v-model="form" @submit="onSubmit">
    <!-- Cloudpost link -->
    <div
      class="w-full block"
      :class="[postId === currentPost.id ? 'opacity-100' : 'opacity-0']"
    >
      <div v-if="canShowFeature('CLOUDPOST_LINK')" class="-mt-8 mb-6">
        <a :href="cloudpostLink" target="_blank">
          View original post on the Cloud
        </a>
      </div>
    </div>
    <!-- /Cloudpost link -->

    <!-- Manager buttons -->
    <div
      v-if="currentPost.id"
      class="w-full flex items-center space-x-2 justify-start lg:justify-between -mt-8 py-4"
    >
      <h3 v-if="isPage">Edit Page</h3>
      <div class="flex space-x-4">
        <!-- Delete button -->
        <button
          type="button"
          class="btn btn-black"
          :class="{ 'btn-cw-cd': isContributor }"
          @click="toggleDeleteModal"
        >
          <span>Delete</span>
        </button>
        <!-- /Delete button -->

        <!-- Submit button -->
        <FormulateInput
          type="submit"
          :disabled="submitDisabled"
          :input-class="[
            isContributor ? 'btn-cw-cd-primary' : 'btn-primary',
            isSubmitting
              ? isContributor
                ? 'thinking'
                : 'thinking-primary cursor-not-allowed'
              : '',
            {
              'opacity-25 border border-truegray-700 cursor-not-allowed':
                submitDisabled,
            },
          ]"
        >
          <span
            v-if="isSubmitting"
            class="w-full h-6 flex items-center justify-center"
          >
            <lottie :options="loadingAnimationOptions" />
          </span>
          <span v-else>
            {{ submitButtonText }}
          </span>
        </FormulateInput>
        <!-- /Submit button -->
      </div>
    </div>
    <!-- /Manager buttons -->

    <!-- Delete modal -->
    <div
      v-if="deleteModalVisible && !isContributor"
      id="showModal"
      class="fixed z-70 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <!-- Modal panel, show/hide based on modal state. -->
        <div
          :class="[
            deleteModalContentsVisible
              ? 'translate-y-0'
              : 'translate-y-full opacity-0',
          ]"
          class="bg-white transform delay-100 w-full sm:max-w-3xl sm:w-full inline-block rounded-lg p-12 sm:mt-8 sm:mb-20 sm:p-16 text-left shadow transition-all align-bottom sm:align-middle overflow-visible relative"
        >
          <h2 class="w-full mb-4 text-center">
            <span>
              {{ deleteModalTitle }}
            </span>
          </h2>

          <p class="mb-4 text-md text-center">
            {{ deleteModalSubtitle }}
          </p>

          <button
            type="button"
            :disabled="deleteDisabled"
            class="btn btn-primary m-auto mt-10 relative"
            @click="onDelete"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie v-if="isDeleting" :options="loadingAnimationOptions" />
            </div>
            <span :class="{ invisible: isDeleting }">Confirm</span>
          </button>

          <button
            type="button"
            class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
            @click="toggleDeleteModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
    <!-- /Delete modal -->

    <!-- PostUpdate content -->
    <div class="w-full flex flex-wrap lg:flex-nowrap">
      <!-- LHS -->
      <div
        class="w-full lg:w-4/12 pr-6 pt-4 pb-8 lg:pb-4 border-b lg:border-b-0 border-truegray-300 space-y-8"
      >
        <!-- Category -->
        <div>
          <FormulateInput
            v-if="canShowFeature('CATEGORY')"
            key="category"
            name="category"
            :validation="isDraft ? '' : 'required'"
          >
            <template #element>
              <InputDropdown
                label="Post category (required)"
                :selected-options-initial="[form.category]"
                :options="CATEGORY_TYPES"
                :placeholder-default="categoryPlaceholder"
                response-selection-id="input-updated-category"
                :margin-bottom="false"
              />
            </template>
          </FormulateInput>
        </div>
        <!-- /Category -->

        <!-- Toggles & Region -->
        <div class="space-y-5">
          <!-- Publish/draft toggle -->
          <div>
            <FormulateInput
              key="status"
              name="status"
              type="toggle"
              :val="form.status"
              :label="
                isContributor ? 'Published' : form.status ? 'Publish' : 'Draft'
              "
            />
            <div v-if="!form.status" class="">
              <p class="text-xs text-truegray-500 italic my-2">
                Required fields are only required if publishing.
              </p>
            </div>
          </div>
          <!-- /Publish/draft toggle -->

          <!-- Share to WordPress -->
          <div v-show="canShowFeature('WORDPRESS')">
            <FormulateInput
              key="shareToWordPress"
              name="shareToWordPress"
              :wrapper-class="['flex items-center justify-start gap-2']"
            >
              <template #element>
                <button
                  type="button"
                  :class="[
                    form.shareToWordPress
                      ? isContributor
                        ? 'bg-cw-alt'
                        : 'bg-primary'
                      : 'bg-gray-200',
                    CLASSES.TOGGLE,
                  ]"
                  role="switch"
                  aria-checked="false"
                  @click="toggleShareToWordPress"
                >
                  <span
                    aria-hidden="true"
                    class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-all ring-0 ease-in-out duration-200"
                    :class="[
                      form.shareToWordPress ? 'translate-x-5' : 'translate-x-0',
                    ]"
                  >
                    <img src="/img/icons/wordpress.png" alt="Wordpress icon" />
                  </span>
                </button>
                <label :class="wordPressConfig.class">
                  {{ wordPressConfig.text }}
                </label>
              </template>
            </FormulateInput>
          </div>
          <!-- /Share to WordPress -->

          <!-- Private toggle -->
          <div
            v-show="canShowFeature('PRIVATE')"
            title="Marking a post as private will hide it from the public posts pages and mean only admins can see it. This can be useful if you want to include sensitive information in a newsletter to specific mail lists."
            class="flex items-center"
          >
            <FormulateInput
              key="private"
              name="private"
              type="toggle"
              label="Private"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              class="w-3 h-3 ml-2 text-truegray-300"
            >
              <path
                fill-rule="evenodd"
                d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <!-- Share to Cloud toggle -->
          <div v-show="canShowFeature('SHARE_TO_CLOUD')">
            <FormulateInput
              key="shareToCloud"
              name="shareToCloud"
              type="toggle"
              :label="shareToCloudText"
            />
          </div>
          <!-- /Share to Cloud toggle -->

          <!-- Region tag -->
          <div v-if="canShowFeature('TAGS_REGION')">
            <FormulateInput
              key="tagsRegion"
              name="tagsRegion"
              validation="valid"
              :validation-rules="{
                valid: () => isDraft || !!form.tagsRegion,
              }"
              :validation-messages="{
                valid: 'Region is required.',
              }"
            >
              <template #element>
                <InputDropdown
                  label="Region (required)"
                  :selected-options-initial="[form.tagsRegion]"
                  :options="tagsRegions"
                  :category="form.category"
                  placeholder-default="Select a region"
                  response-selection-id="input-updated-tags-region"
                  :margin-bottom="false"
                />
              </template>
            </FormulateInput>
          </div>
          <!-- /Region tag -->
        </div>
        <!-- /Toggles -->

        <!-- Event fields -->
        <div v-if="canShowFeature('EVENTS')" class="space-y-3">
          <p class="uppercase text-sm font-semibold">Event fields</p>
          <div class="space-y-4">
            <!-- Event dates -->
            <FormulateInput
              key="dateRange"
              name="dateRange"
              label="Event date(s) (required)"
              :validation="isDraft ? '' : 'required'"
              validation-name="Event date"
              error-behavior="value"
            >
              <template #label="context">
                <h6 :for="context.id" class="text-truegray-700">
                  {{ context.label }}
                </h6>
              </template>
              <template #element>
                <date-picker
                  v-model="form.dateRange"
                  range
                  value="datetime"
                  value-type="format"
                  format="DD-MM-YYYY"
                />
              </template>
            </FormulateInput>
            <!-- /Event dates -->

            <!-- Event time -->
            <FormulateInput
              key="eventStartTime"
              name="eventStartTime"
              label="Start time (optional)"
            >
              <template #label="context">
                <h6 :for="context.id" class="text-truegray-700">
                  {{ context.label }}
                </h6>
              </template>
              <template #element>
                <date-picker
                  v-model="form.eventStartTime"
                  format="hh:mm a"
                  value-type="format"
                  type="time"
                  :minute-step="15"
                />
              </template>
            </FormulateInput>
            <!-- /Event time -->

            <!-- Event venue title -->
            <FormulateInput
              key="eventVenueTitle"
              name="eventVenueTitle"
              label="Venue name (required)"
              :validation="isDraft ? '' : 'required'"
              validation-name="Venue name"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                </h6>
              </template>
            </FormulateInput>
            <!-- /Event venue title -->

            <!-- Event venue address -->
            <FormulateInput
              key="eventVenueAddress"
              name="eventVenueAddress"
              label="Venue address (optional)"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                </h6>
              </template>
            </FormulateInput>
            <!-- /Event venue address -->
          </div>
        </div>
        <!-- /Events fields -->

        <!-- Job fields -->
        <div v-if="canShowFeature('JOBS')" class="space-y-3">
          <p class="uppercase text-sm font-semibold mb-2">Job fields</p>
          <div class="space-y-4">
            <!-- Expiry date -->
            <div class="w-full relative mb-4">
              <h6 class="text-truegray-700 group flex items-center">
                Expiration date (optional)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="h-3 w-3 ml-2"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div :class="CLASSES.INFO">
                  You can select a date range if applications have not yet
                  started, or simply select an end date and CareerWise will
                  automatically set the start date as today.
                </div>
              </h6>
              <date-picker
                v-model="form.dateRange"
                range
                value="datetime"
                value-type="format"
                format="DD-MM-YYYY"
              />
            </div>
            <!-- /Expiry date -->

            <!-- Job type -->
            <FormulateInput
              key="jobType"
              name="jobType"
              :validation="isDraft ? '' : 'required'"
              validation-name="Job type"
            >
              <template #element>
                <InputDropdown
                  label="Job type (required)"
                  :selected-options-initial="[form.jobType]"
                  :options="JOB_TYPES"
                  placeholder-default="Select a job type"
                  response-selection-id="input-updated-job-type"
                  :margin-bottom="false"
                />
              </template>
            </FormulateInput>
            <!-- /Job type -->

            <!-- Job employer -->
            <FormulateInput
              key="jobEmployer"
              name="jobEmployer"
              label="Employer (required)"
              :validation="isDraft ? '' : 'required'"
              validation-name="Job employer"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                </h6>
              </template>
            </FormulateInput>
            <!-- /Job employer -->
          </div>
        </div>
        <!-- /Job fields -->

        <!-- General fields -->
        <div class="space-y-3">
          <p class="uppercase text-sm font-semibold">General fields</p>
          <div class="space-y-4">
            <!-- Post title -->
            <FormulateInput
              key="title"
              name="title"
              label="Title (required)"
              validation="required"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                </h6>
              </template>
            </FormulateInput>
            <!-- /Post title -->

            <!-- General tags -->
            <FormulateInput
              v-if="canShowFeature('TAGS_GENERAL')"
              key="tagsGeneral"
              name="tagsGeneral"
              :validation="isDraft ? '' : 'valid'"
              :validation-rules="{
                valid: validateRequiredTags,
              }"
              :validation-messages="{
                valid: 'At least one general or industry tag is required.',
              }"
            >
              <template #element>
                <InputDropdown
                  label="General tags (1 tag type required)"
                  :selected-options-initial="form.tagsGeneral"
                  :options="tagsGeneral"
                  :margin-bottom="false"
                  :multiselect="true"
                  response-selection-id="input-updated-tags-general"
                />
              </template>
            </FormulateInput>
            <!-- /General tags -->

            <!-- Industry tags -->
            <FormulateInput
              v-if="canShowFeature('TAGS_INDUSTRIES')"
              key="tagsIndustries"
              name="tagsIndustries"
              :validation="isDraft ? '' : 'valid'"
              :validation-rules="{
                valid: validateRequiredTags,
              }"
              :validation-messages="{
                valid: 'At least one general or industry tag is required.',
              }"
            >
              <template #element>
                <InputDropdown
                  label="Industry tags (1 tag type required)"
                  :selected-options-initial="form.tagsIndustries"
                  :options="tagsIndustries"
                  :margin-bottom="false"
                  :multiselect="true"
                  response-selection-id="input-updated-tags-industries"
                />
              </template>
            </FormulateInput>
            <!-- /Industry tags -->

            <!-- Year tags -->
            <FormulateInput
              v-if="canShowFeature('TAGS_YEARS')"
              key="tagsYears"
              name="tagsYears"
            >
              <template #element>
                <InputDropdown
                  v-if="!isPage"
                  label="Year groups (optional)"
                  :selected-options-initial="form.tagsYears"
                  :options="tagsYears"
                  :margin-bottom="false"
                  :multiselect="true"
                  response-selection-id="input-updated-tags-years"
                />
              </template>
            </FormulateInput>
            <!-- /Year tags -->

            <!-- Post link title -->
            <FormulateInput
              key="linkTitle"
              name="linkTitle"
              label="Link title (optional)"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="flex items-center group absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-3 w-3 ml-2"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <div :class="CLASSES.INFO">
                    Creates a button linking to an external resource or settings
                  </div>
                </h6>
              </template>
            </FormulateInput>
            <!-- /Post link title -->

            <!-- Post link URL -->
            <FormulateInput
              key="linkURL"
              name="linkURL"
              label="Link URL (optional)"
              :validation="isDraft ? '' : 'valid'"
              :validation-rules="{
                valid: ({ value }) => validateLinkURL(value),
              }"
              :validation-messages="{
                valid: 'Please provide a valid URL.',
              }"
              :wrapper-class="['relative']"
              :input-class="[CLASSES.INPUT]"
            >
              <template #label="context">
                <h6
                  :for="context.id"
                  class="absolute ml-4 mt-1 text-truegray-700"
                >
                  {{ context.label }}
                </h6>
              </template>
            </FormulateInput>
            <!-- /Post link URL -->

            <!-- Resources -->
            <div class="pt-6">
              <!-- Resources input (button) -->
              <FormulateInput
                key="resources"
                name="resources"
                label="Post resources"
                help="You can select up to 10 files."
              >
                <template #element>
                  <div class="my-2">
                    <!-- Resources button -->
                    <button
                      type="button"
                      class="btn btn-black m-0"
                      :class="{
                        'btn-cw-cd': isContributor,
                        'opacity-25 border border-truegray-700 cursor-not-allowed':
                          !canAddResources,
                      }"
                      :disabled="!canAddResources"
                      @click="onClickResources"
                    >
                      Add resources
                    </button>
                    <!-- /Resources button -->
                  </div>
                </template>
              </FormulateInput>
              <!-- /Resources input (button) -->

              <!-- Resources list -->
              <div v-if="form.resources?.length" class="mt-4">
                <h6>Attached resources</h6>
                <vue-nestable v-model="form.resources" :max-depth="1">
                  <vue-nestable-handle
                    slot-scope="{ item }"
                    :item="item"
                    class="flex justify-start w-full space-x-2 bg-gray-50 p-2 rounded border my-1"
                  >
                    <div class="flex justify-start w-full space-x-4">
                      <button
                        v-if="form.resources.length > 1"
                        class="drag"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5 hover:text-primary transition-all duration-100"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M3 7a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 13a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                      <span>{{ item.title }}</span>
                    </div>

                    <div class="group">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 text-gray-300 hover:text-gray-500 cursor-pointer transition-all duration-100"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        @click="detachResource(item.id)"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div
                        class="ml-2 text-xs absolute left-full top-0 w-24 z-50 border border-truegray-300 rounded py-2 px-4 opacity-0 transition-all duration-300 transform pointer-events-none group-hover:opacity-100 bg-white text-center"
                      >
                        Remove resource
                      </div>
                    </div>
                  </vue-nestable-handle>
                </vue-nestable>
              </div>
              <!-- /Resources list -->
            </div>
            <!-- /Resources -->

            <!-- Connections (people) -->
            <div class="pt-6">
              <!-- Connections input (button) -->
              <FormulateInput
                key="connections"
                name="connections"
                label="Connections"
                :help="
                  isContributor
                    ? ''
                    : 'Local connections will NOT be shared to the cloud if you opt to share this post. CareerWise connections will still be synced.'
                "
              >
                <template #element>
                  <div class="my-2">
                    <button
                      type="button"
                      class="btn btn-black m-0"
                      @click="showConnectionsModal"
                    >
                      {{ connectionsButtonText }}
                    </button>
                  </div>
                </template>
              </FormulateInput>
              <!-- /Connections input (button) -->

              <!-- Connections list -->
              <div
                v-if="form.connections?.length"
                class="gap-4 flex flex-col my-4"
              >
                <ListConnection
                  v-for="connection in form.connections"
                  :key="connection.id"
                  :connection="connection"
                />
              </div>
              <!-- /Connections list -->
            </div>
            <!-- /Connections (people) -->
          </div>
        </div>
        <!-- /General fields -->
      </div>
      <!-- /LHS -->

      <!-- RHS -->
      <div
        class="w-full lg:w-8/12 lg:border-l border-truegray-300 lg:pl-6 pb-4 pt-8 lg:pt-4 space-y-8"
      >
        <!-- Featured image fields -->
        <div class="space-y-4">
          <!-- Featured image -->
          <FormulateInput
            key="featuredImage"
            name="featuredImage"
            type="image"
            label="Featured image (optional)"
            help="For best results, use a .jpg or .png under 1MB, with a 4:3 ratio and at least 700px wide."
            wrapper-class="formulate-input-wrapper flex flex-col h-full w-full"
            :label-class="['text-truegray-700']"
          >
            <template #element>
              <div class="flex items-center w-full">
                <div
                  :class="CLASSES.IMAGE.WRAPPER"
                  :style="{ backgroundImage: `url(${featuredImageURL})` }"
                >
                  <button
                    type="button"
                    :class="CLASSES.IMAGE.BUTTON"
                    @click="onClickFeaturedImage"
                  >
                    {{ featuredImageButtonText }}
                  </button>
                </div>
              </div>
            </template>
          </FormulateInput>
          <!-- /Featured image -->

          <!-- Remove featured image -->
          <div
            v-if="canShowFeature('REMOVE_IMAGE')"
            class="relative flex items-start"
          >
            <FormulateInput
              key="featuredImageRemove"
              name="featuredImageRemove"
              type="checkbox"
              label="Remove featured image"
              :label-class="['text-truegray-700']"
            />
          </div>
          <!-- /Remove featured image -->
        </div>
        <!-- /Featured image fields -->

        <!-- Description -->
        <div>
          <FormulateInput
            name="description"
            type="textarea"
            rows="3"
            label="Description (optional)"
            help="Word limit is 160 characters."
            validation="max:160,length"
            :validation-messages="{
              max: 'You are over the word limit.',
            }"
            error-behavior="live"
            :label-class="['text-truegray-700']"
            :input-class="[CLASSES.TEXTAREA]"
            :help-class="['mt-1 mb-0']"
          />
        </div>
        <!-- /Description -->

        <!-- Body/content -->
        <div>
          <FormulateInput
            key="body"
            name="body"
            label="Post content (required)"
            :validation="isDraft ? '' : 'valid'"
            :validation-rules="{
              valid: ({ value }) => {
                const strippedContent = value.replace(/<[^>]*>?/gm, '')
                return !!strippedContent.trim()
              },
            }"
            :validation-messages="{
              valid: 'Post content is required.',
            }"
            error-behavior="value"
            :label-class="['text-truegray-700']"
          >
            <template #element>
              <p
                v-if="badBrowser"
                class="text-red-500 text-xs my-1 w-full flex justify-start items-center space-x-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  >We recommend using Chrome to best utilise all features in
                  this editor.</span
                >
              </p>

              <Editor
                :initial-content="form.body"
                :active-features="[
                  'headings',
                  'bold',
                  'italic',
                  'strike',
                  'highlight',
                  'text_colour',
                  'underline',
                  'h1',
                  'h2',
                  'h3',
                  'alignments',
                  'alignLeft',
                  'alignCenter',
                  'alignRight',
                  'lists',
                  'bulletList',
                  'orderedList',
                  'blockquote',
                  'code',
                  'codeBlock',
                  'link',
                  'remove_link',
                  'image',
                  'tables',
                  'table',
                  'horizontalRule',
                  'undo',
                  'redo',
                ]"
                class="mt-1"
                @update-content="updateEditorContent"
              />
              <!-- 
                'wand',
                'paste_without_formatting',
                'paste_from_email'
              -->
            </template>
          </FormulateInput>
        </div>
        <!-- /Body/content -->
      </div>
      <!-- /RHS -->
    </div>
    <!-- /PostUpdate content -->
  </FormulateForm>
</template>

<script>
import { defineComponent, computed, watch, ref, onMounted, nextTick } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { VueNestable, VueNestableHandle } from 'vue-nestable'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import { CATEGORY_TYPES, JOB_TYPES } from '../../../../../js/util/constants.js'
import {
  validateURL,
  validateURLProtocol,
  updateURLWithProtocol,
  checkPositiveInteger,
  isMenuItemLocked,
} from '../../../../../js/util/helper.js'
// Components
import Editor from '../../components/editor-wysiwyg/Editor.vue'
import InputDropdown from '../../../../../shared/components/InputDropdown.vue'
import ListConnection from '../../../../shared/connections/components/ListConnection.vue'

export default defineComponent({
  name: 'PostUpdate',

  components: {
    DatePicker,
    InputDropdown,
    VueNestable,
    VueNestableHandle,
    Editor,
    ListConnection,
  },

  props: {
    postId: {
      type: Number,
      required: true,
    },

    isLoading: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['update-loading'],

  setup(props, { emit }) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const badBrowser = computed(
      () => !!document.getElementsByTagName('body')[0].dataset.badbrowser,
    )
    const settings = computed(() => store.getters['shared/getSettings'])
    const userAccess = computed(() => store.getters['shared/getUserAccess'])
    const isContributor = computed(() => store.getters['shared/isContributor'])

    const isDraft = computed(() => !form.value.status)

    // Form
    const FORM_DEFAULT_VALUES = {
      // LHS
      category: null,
      status: true,
      shareToWordPress: false,
      shareToCloud: false,
      private: false,
      tagsRegion: null,
      title: null,
      // Jobs/Events
      dateRange: null,
      // Events
      eventStartTime: null,
      eventVenueTitle: null,
      eventVenueAddress: null,
      // Jobs
      jobType: null,
      jobEmployer: null,
      // Tags
      tagsGeneral: [],
      tagsIndustries: [],
      tagsYears: [],
      // Link
      linkTitle: null,
      linkURL: null,
      // Attached
      resources: [],
      connections: [],
      // RHS
      featuredImage: null,
      featuredImageRemove: false,
      description: null,
      body: null,
    }

    const TOGGLE_MAP = {
      shareToWordPress: 'shared_to_wp',
      shareToCloud: 'cloud_post',
    }

    const FORM_MAP = {
      // LHS
      category: 'category',
      status: 'status',
      private: 'private',
      ...TOGGLE_MAP,
      title: 'title',
      // Jobs/Events
      dateRange: 'event_date_range',
      // Events
      eventStartTime: 'event_start_at_time',
      eventVenueTitle: 'event_venue_title',
      eventVenueAddress: 'event_venue_address',
      // Jobs
      jobType: 'job_type',
      jobEmployer: 'job_employer',
      // Link
      linkTitle: 'link_title',
      linkURL: 'link_url',
      // Attached
      resources: 'resources',
      connections: 'contacts',
      // RHS
      featuredImage: 'image_featured',
      description: 'description',
      body: 'body',
    }

    const form = ref(FORM_DEFAULT_VALUES)

    // Features
    const CLASSES = computed(() => {
      const imageButtonClass = isContributor.value
        ? 'btn-cw-alt-trans'
        : 'btn-trans-white'
      const fontClass = isContributor.value ? '' : 'font-primary'

      return {
        INPUT: `pt-3 shadow ${fontClass}`,
        TEXTAREA: `mt-1 ${fontClass}`,
        TOGGLE:
          'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-all ease-in-out duration-200 focus:outline-none',
        IMAGE: {
          WRAPPER:
            'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20',
          BUTTON: `z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn ${imageButtonClass} text-white m-0 ml-6`,
        },

        INFO: 'absolute left-full -top-2.5 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',
      }
    })

    const featureMap = computed(() => {
      return {
        CLOUDPOST_LINK: !!cloudpostId.value,
        CATEGORY: !isPage.value,
        EVENTS: isEvent.value,
        JOBS: isJob.value,
        PRIVATE: !isContributor.value && !form.value.shareToCloud,
        WORDPRESS:
          !isPage.value &&
          form.value.status &&
          !!wordPressConnections.value?.wordpress?.categoryId,
        SHARE_TO_CLOUD:
          !isContributor.value &&
          !isPage.value &&
          form.value.status &&
          !form.value.private,
        TAGS_REGION: !!form.value.shareToCloud,
        TAGS_GENERAL: !isPage.value,
        TAGS_INDUSTRIES: !isPage.value,
        TAGS_YEARS: !isPage.value,
        REMOVE_IMAGE: !!form.value.featuredImage,
        DEFAULT: true,
      }
    })

    const canShowFeature = (type = 'DEFAULT') => {
      return featureMap.value[type]
    }

    // Post
    const currentPost = computed(() => store.getters['shared/getCurrentPost'])
    const isPage = computed(() => {
      return currentPost.value.category === 0
    })
    const isJob = computed(() => form.value.category === 2)
    const isEvent = computed(() => form.value.category === 3)

    // Cloudpost
    const cloudpostId = computed(
      () => store.getters['shared/getCurrentPostCloudpostId'],
    )
    const cloudpostLink = computed(
      () => store.getters['shared/getCurrentPostCloudpostLink'],
    )

    // Category
    const categoryKey = computed(() => (isPage.value ? 'page' : 'post'))
    const categoryPlaceholder = computed(() => {
      return (
        CATEGORY_TYPES.find((opt) => opt.value === form.value.category)?.text ||
        ''
      )
    })

    // Status
    watch(
      () => form.value.status,
      (val) => {
        if (!isContributor.value) {
          const keys = ['shareToWordPress', 'shareToCloud']

          keys.forEach((key) => {
            if (val) {
              // Set back to original values
              initForm(TOGGLE_MAP)
            } else {
              form.value[key] = FORM_DEFAULT_VALUES[key]
            }
          })
        }
      },
    )

    watch(
      () => form.value.private,
      (val) => {
        if (!isContributor.value) {
          const keys = ['shareToCloud']

          keys.forEach((key) => {
            if (val) {
              // Set back to original values
              initForm(TOGGLE_MAP)
            } else {
              form.value[key] = FORM_DEFAULT_VALUES[key]
            }
          })
        }
      },
    )

    // WordPress
    const wordPressConnections = computed(
      () => store.getters['careerwise/getConnections'],
    )
    const shareToWordPressInitial = computed(
      () => currentPost.value?.[FORM_MAP.shareToWordPress],
    )
    const wordPressConfig = computed(() => {
      const deleteFromWordPress =
        !form.value.shareToWordPress && shareToWordPressInitial.value
      return {
        class: deleteFromWordPress ? 'text-sm text-red-400' : 'text-sm',
        text: deleteFromWordPress
          ? 'Delete from WordPress'
          : form.value.shareToWordPress
          ? 'Share to WordPress'
          : `Don't share to WordPress`,
      }
    })
    const toggleShareToWordPress = () => {
      form.value.shareToWordPress = !form.value.shareToWordPress
    }

    // Share to Cloud
    const shareToCloudText = computed(() => {
      return form.value.shareToCloud ? 'Share to Cloud' : `Don't share to Cloud`
    })
    watch(
      () => form.value.shareToCloud,
      () => {
        initTags(true)
      },
    )

    // Jobs
    const validateLinkURL = (val) => {
      if (val) {
        return validateURL(val)
      }
      return true
    }
    watch(
      () => form.value.linkURL,
      (val) => {
        if (val && !validateURLProtocol(val)) {
          form.value.linkURL = updateURLWithProtocol(val)
        }
      },
    )

    // Tags
    const tagsRegions = computed(() => store.getters['shared/getTagsRegions'])
    const tagsGeneral = computed(() => store.getters['shared/getTagsGeneral'])
    const tagsYears = computed(() => store.getters['shared/getTagsYears'])
    const tagsIndustries = computed(
      () => store.getters['shared/getTagsIndustries'],
    )
    const tagsAll = computed(() => {
      return [
        form.value.tagsRegion,
        ...form.value.tagsGeneral,
        ...form.value.tagsIndustries,
        ...form.value.tagsYears,
      ].filter((val) => !!val)
    })

    const validateRequiredTags = ({ getFormValues }) => {
      const { tagsGeneral, tagsIndustries } = getFormValues()
      return !!tagsGeneral?.length || !!tagsIndustries?.length
    }

    // Featured image
    const featuredImageInitial = computed(
      () => currentPost.value?.[FORM_MAP.featuredImage],
    )
    const featuredImageURL = ref(featuredImageInitial.value)

    const featuredImageUpdated = computed(() => {
      if (!featuredImageInitial.value) {
        return !!form.value.featuredImage
      }

      return form.value.featuredImage !== featuredImageInitial.value
    })
    const featuredImageButtonText = computed(() => {
      if (featuredImageUpdated.value) {
        return 'Cancel'
      }
      return 'Change'
    })
    const onClickFeaturedImage = () => {
      if (featuredImageUpdated.value) {
        resetFeaturedImage()
      } else {
        resourcesSearchModalType.value = 'featured_image'
      }
    }
    const assignResourceImage = (resource) => {
      form.value.featuredImage = resource.id
      featuredImageURL.value = resource.file_url
      form.value.featuredImageRemove = false
    }
    const resetFeaturedImage = () => {
      form.value.featuredImage = featuredImageInitial.value
      featuredImageURL.value = form.value.featuredImage
    }

    // Resources
    const resourcesSearchModalType = ref(null)
    watch(resourcesSearchModalType, (val) => showResourcesSearchModal(val))

    const canAddResources = computed(() => {
      return form.value.resources?.length < 10
    })
    const onClickResources = () => {
      resourcesSearchModalType.value = 'resources'
    }
    const showResourcesSearchModal = (val) => {
      EventBus.$emit('modal_state_changing', val || false)
      EventBus.$emit(
        'showResourcesSearchModal',
        resourcesSearchModalType.value || false,
      )
    }

    const assignResource = (resource) => {
      const found = form.value.resources?.find((r) => r.id === resource.id)
      if (!found) {
        form.value.resources.push(resource)
      }
    }

    const detachResource = (id) => {
      const index = form.value.resources?.findIndex((r) => r.id === id)
      if (index > -1) {
        form.value.resources.splice(index, 1)
      }
    }

    // Connections (people)
    const selectedConnections = computed(
      () => store.getters['connections/getPostSelectedConnections'],
    )
    watch(selectedConnections, () => {
      form.value.connections = selectedConnections.value
    })
    const connectionsButtonText = computed(() => {
      return form.value.connections?.length
        ? 'Edit connections'
        : 'Add connections'
    })
    const showConnectionsModal = () => {
      EventBus.$emit('showModal', {
        action: 'attach-connections',
        data: {
          context: 'POSTS',
          postId: props.postId,
        },
      })
    }

    // Editor/body
    const updateEditorContent = (val) => {
      form.value.body = val
    }

    // Submit
    const isSubmitting = ref(false)
    const submitDisabled = computed(
      () => isSubmitting.value || isDeleting.value,
    )
    const submitButtonText = computed(() =>
      isPage.value ? 'Save page' : 'Save post',
    )

    const onSubmit = async () => {
      isSubmitting.value = true
      const featuredImageRemove = form.value.featuredImageRemove
      const payload = {
        post_id: currentPost.value.id,
        tags: tagsAll.value,
        ...(featuredImageRemove && { imageRemove: featuredImageRemove }),
      }

      Object.entries(FORM_MAP).forEach(([key, value]) => {
        // If featured image has not been updated, do NOT pass it through -
        // backend stores a reference to the file path not an actual ID.
        if (key !== 'featuredImage' || featuredImageUpdated.value) {
          const formValue = form.value[key]

          // For connections and resources, map values to their IDs.
          let data = ['connections', 'resources'].includes(key)
            ? formValue?.map((val) => val.id)
            : formValue

          if (!data && data !== false) {
            // 0 is a valid value (for some select inputs): allow it!
            if (!checkPositiveInteger(data)) {
              data = null
            }
          }

          payload[value] = data
        }
      })

      const action = isContributor.value
        ? 'contributors/dispatchUpdatePost'
        : 'careerwise/dispatchUpdatePost'

      await store.dispatch(action, payload)
      isSubmitting.value = false
      EventBus.$emit('reloadWebsiteSettings')
    }

    // Delete modal
    const isDeleting = ref(false)
    const deleteModalVisible = ref(false)
    const deleteModalContentsVisible = ref(false)
    const deleteDisabled = computed(
      () => isSubmitting.value || isDeleting.value,
    )
    const deleteModalTitle = computed(() => `Delete ${categoryKey.value}`)
    const deleteModalSubtitle = computed(
      () => `Do you want to delete this ${categoryKey.value} forever?`,
    )
    const toggleDeleteModal = () => {
      deleteModalVisible.value = !deleteModalVisible.value
    }
    watch(deleteModalVisible, (val) => {
      if (isContributor.value) {
        // Use the post delete modal component, passing through post id data
        if (val) {
          EventBus.$emit('showModal', {
            action: 'post-delete',
            data: {
              postId: props.postId,
            },
          })
        }
      } else {
        // Old logic - @TODO update to use the modal (above) and remove delete handlers/listeners
        EventBus.$emit('modal_state_changing', val)
        if (!val) {
          deleteModalContentsVisible.value = false
        } else {
          // Timeout for transition
          setTimeout(() => {
            deleteModalContentsVisible.value = true
          }, 100)
        }
      }
    })

    const handleDeletePost = () => {
      isDeleting.value = false
      EventBus.$emit('modal_state_changing', false)

      // Route to appropriate page
      if (!isPage.value) {
        const path = isContributor.value ? '/cloud/posts' : '/posts'
        if (route.path !== path) {
          router.push({ path })
        }
      } else {
        router.push({ path: '/manager/pages' })
      }
    }

    const onDelete = async () => {
      if (!props.postId || !userAccess.value) {
        return
      }

      isDeleting.value = true
      await store.dispatch('careerwise/dispatchDeletePost', props.postId)
    }

    const initTags = async (regionOnly = false) => {
      const selectedTags = currentPost.value.tags
      let tagsConfig = {
        tagsRegion: { multiple: false, tags: tagsRegions.value },
      }

      if (!regionOnly) {
        tagsConfig = {
          ...tagsConfig,
          tagsGeneral: { multiple: true, tags: tagsGeneral.value },
          tagsIndustries: { multiple: true, tags: tagsIndustries.value },
          tagsYears: { multiple: true, tags: tagsYears.value },
        }
      }

      Object.entries(tagsConfig).forEach(([key, { multiple, tags }]) => {
        const tagsArr = selectedTags
          ?.filter((selectedTag) => {
            return tags?.find((tag) => tag.value === selectedTag.id)
          })
          .map((tag) => tag.id)

        form.value[key] = (multiple ? tagsArr : tagsArr?.[0]) || null
      })

      await nextTick()
    }

    const initForm = async (formItems = FORM_MAP) => {
      Object.entries(formItems).forEach(([key, value]) => {
        // Set form data to default value if null or undefined (but not if false, "", or 0).
        let formValue = currentPost.value[value] ?? FORM_DEFAULT_VALUES[key]

        // If the featured image is any falsy value, explicitly set it to the default (null)
        // as backend returns false for some reason!
        if (key === 'featuredImage' && !formValue) {
          formValue = FORM_DEFAULT_VALUES[key]
        }

        // We have to cast job type to an integer for the input (if it is set!).
        // It is stored as a string in the db - not keen to fix/break all data/other
        // templates by migrating/updating backend.
        if (key === 'jobType' && formValue) {
          formValue = Number(formValue)
        }

        // We want to default status to true to match
        // previous behaviour and customer expected behaviour
        if (key === 'status' && !formValue) {
          formValue = FORM_DEFAULT_VALUES[key]
        }

        // Update the form data with the new value.
        form.value[key] = formValue

        // Update connections in the store for the modal.
        if (key === 'connections') {
          store.dispatch(
            'connections/dispatchUpdateSelectedPostConnections',
            formValue,
          )
        }
      })

      if (isContributor.value) {
        form.value.shareToCloud = true
      }

      await initTags()
      await nextTick()
    }

    const runUpdates = async () => {
      await store.dispatch('shared/runUpdates')
    }

    onMounted(async () => {
      emit('update-loading', true)
      // Route to hootmail if posts are locked from migrations missing
      if (isMenuItemLocked('New Post', settings.value)) {
        router.push({ name: 'Hootmail' })
      }
      const action = isContributor.value
        ? 'contributors/dispatchGetPost'
        : 'shared/dispatchUpdateCurrentPost'
      await store.dispatch(action, props.postId)
      await initForm()

      if (!isContributor.value) {
        await runUpdates()
      }

      emit('update-loading', false)

      /** Listeners **/
      // Resources listeners
      EventBus.$on('closeResourceModal', () => {
        resourcesSearchModalType.value = null
      })

      EventBus.$on('assignResource', (resource) => {
        assignResource(resource)
        resourcesSearchModalType.value = null
      })

      EventBus.$on('assignResourceImage', (resource) => {
        assignResourceImage(resource)
        resourcesSearchModalType.value = null
      })

      // Delete listener
      EventBus.$on('post-delete', (response) => handleDeletePost(response))

      // General fields listeners
      EventBus.$on('input-updated-category', (val) => {
        form.value.category = val
      })

      EventBus.$on('input-updated-job-type', (val) => {
        form.value.jobType = val
      })

      // Tags listeners
      EventBus.$on('input-updated-tags-region', (val) => {
        form.value.tagsRegion = val
      })

      EventBus.$on('input-updated-tags-general', (val) => {
        form.value.tagsGeneral = val
      })

      EventBus.$on('input-updated-tags-industries', (val) => {
        form.value.tagsIndustries = val
      })

      EventBus.$on('input-updated-tags-years', (val) => {
        form.value.tagsYears = val
      })
    })

    return {
      // Config
      loadingAnimationOptions,
      badBrowser,
      settings,
      isContributor,

      // Current post
      CATEGORY_TYPES,
      currentPost,
      isPage,
      isEvent,
      isJob,
      isDraft,

      // Cloudpost
      cloudpostId,
      cloudpostLink,

      // Features
      CLASSES,
      canShowFeature,

      // Form
      form,

      // Category
      categoryPlaceholder,

      // WordPress
      wordPressConfig,
      toggleShareToWordPress,

      // Share to Cloud
      shareToCloudText,

      // Jobs
      JOB_TYPES,
      validateLinkURL,

      // Tags
      tagsRegions,
      tagsGeneral,
      tagsIndustries,
      tagsYears,
      validateRequiredTags,

      // Resources
      canAddResources,
      onClickResources,
      detachResource,

      // Connections (people)
      connectionsButtonText,
      showConnectionsModal,

      // Featured image
      featuredImageURL,
      featuredImageButtonText,
      onClickFeaturedImage,

      // Editor/body
      updateEditorContent,

      // Submit
      submitDisabled,
      submitButtonText,
      isSubmitting,
      onSubmit,

      // Delete modal
      deleteModalVisible,
      deleteModalContentsVisible,
      deleteDisabled,
      deleteModalTitle,
      deleteModalSubtitle,
      toggleDeleteModal,
      isDeleting,
      onDelete,
    }
  },
})
</script>

<style scoped>
.banner-changer::after {
  background: #000;
  opacity: 0.6;
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 0.25rem;
}

.mx-datepicker {
  width: 100%;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
}
</style>
